/**
 * AlternativeUses
 * ---------------
 * The "alternative uses" section on /provider. Renders below the funded /
 * referral-driven programs list, clearly separated by a section note, so an
 * owner can compare a program against the ordinary and commercial options
 * the property could also serve.
 *
 * State discipline (never soften this):
 *   - "public_figure"     -> a sourced figure block with citation
 *   - "built_with_owner"  -> no number shown, CTA drives to owner-specific model
 *   - "unknown"           -> no automated data available, CTA drives elsewhere
 *
 * Every card ends in a CTA that funnels into the same owner_leads capture,
 * carrying the clicked_use_type slug so the follow-up person knows which
 * card triggered the conversation.
 *
 * No layout ranking. No "recommended" badge. The section note explicitly
 * says this is informational, not advisory.
 */
import { ArrowRight, BookOpen, Info, HelpCircle } from "lucide-react";

const STATE_LABEL_EN = {
  public_figure: "Sourced public figure",
  built_with_owner: "We build this with you",
  unknown: "Not shown",
};
const STATE_LABEL_ES = {
  public_figure: "Cifra pública con fuente",
  built_with_owner: "Lo construimos con usted",
  unknown: "No se muestra",
};

const STATE_ICON = {
  public_figure: BookOpen,
  built_with_owner: Info,
  unknown: HelpCircle,
};

function StateChip({ state, lang }) {
  const cls =
    state === "public_figure"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : state === "built_with_owner"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : "bg-slate-100 text-slate-700 border-slate-200";
  const Icon = STATE_ICON[state] || Info;
  const label = (lang === "es" ? STATE_LABEL_ES : STATE_LABEL_EN)[state] || state;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${cls}`} data-testid={`alt-state-${state}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function FigureBlock({ figure, lang }) {
  if (!figure) return null;
  const title = lang === "es" ? figure.figure_title_es : figure.figure_title_en;
  const rows = lang === "es" ? figure.rows_es : figure.rows_en;
  const caveats = lang === "es" ? figure.caveats_es : figure.caveats_en;
  const effective = lang === "es" ? figure.source_effective_period_es : figure.source_effective_period_en;
  return (
    <div className="mt-3 border border-slate-200 rounded-md overflow-hidden bg-slate-50" data-testid="alt-figure-block">
      <div className="px-3 py-2 bg-slate-900 text-white text-xs font-semibold leading-snug">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
              <td className="px-3 py-2 text-slate-700">{r.label}</td>
              <td className="px-3 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">{r.value_display}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[11px] text-slate-600 border-t border-slate-200 bg-white">
        <div>
          <span className="font-semibold">{lang === "es" ? "Fuente:" : "Source:"}</span>{" "}
          <a href={figure.source_url} target="_blank" rel="noopener noreferrer" className="underline text-slate-700 hover:text-slate-900" data-testid="alt-figure-source-link">
            {figure.source_name}
          </a>
          {" · "}
          <span>{effective}</span>
        </div>
      </div>
      <ul className="px-3 py-2 space-y-1.5 text-[12px] text-slate-700 border-t border-slate-200 bg-slate-50 leading-relaxed">
        {caveats.map((c, i) => (
          <li key={i} className="flex gap-1.5"><span className="text-slate-400 mt-0.5">·</span><span>{c}</span></li>
        ))}
      </ul>
    </div>
  );
}

export default function AlternativeUses({ lang = "en", items = [], hmfaDisplay = "", sectionNote = "", onCta }) {
  if (!items || items.length === 0) return null;
  const heading =
    lang === "es"
      ? "Usos alternativos para esta propiedad"
      : "Alternative uses for this property";
  const areaLabel =
    lang === "es"
      ? `Cifras públicas basadas en el área: ${hmfaDisplay}`
      : `Public figures based on area: ${hmfaDisplay}`;

  return (
    <section className="mt-10" data-testid="provider-alternatives">
      <h2 className="text-xl md:text-2xl font-bold leading-tight" data-testid="provider-alternatives-title">
        {heading}
      </h2>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed" data-testid="provider-alternatives-note">
        {sectionNote}
      </p>
      {hmfaDisplay && (
        <p className="mt-1 text-xs text-slate-500" data-testid="provider-alternatives-area">{areaLabel}</p>
      )}

      <div className="mt-4 space-y-3">
        {items.map((c) => {
          const title = lang === "es" ? c.title_es : c.title_en;
          const summary = lang === "es" ? c.summary_es : c.summary_en;
          const cta = lang === "es" ? c.cta_es : c.cta_en;
          const unknownBody =
            c.state === "unknown"
              ? lang === "es"
                ? c.unknown_body_es
                : c.unknown_body_en
              : "";
          return (
            <article
              key={c.slug}
              className="p-4 bg-white border border-slate-200 rounded-lg"
              data-testid={`alt-card-${c.slug}`}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-bold text-base md:text-lg leading-tight flex-1" data-testid={`alt-card-title-${c.slug}`}>
                  {title}
                </h3>
                <StateChip state={c.state} lang={lang} />
              </div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{summary}</p>

              {c.state === "public_figure" && <FigureBlock figure={c.figure} lang={lang} />}

              {c.state === "unknown" && unknownBody && (
                <p className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed" data-testid={`alt-unknown-body-${c.slug}`}>
                  {unknownBody}
                </p>
              )}

              <button
                type="button"
                onClick={() => onCta && onCta(c.slug)}
                className="mt-3 inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-md px-4 py-2.5 min-h-[44px] w-full sm:w-auto"
                data-testid={`alt-cta-${c.slug}`}
              >
                {cta} <ArrowRight className="w-4 h-4" />
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}
